import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Zap, ArrowRight, Check, Star, ChevronDown, ChevronUp,
  Users, Briefcase, BookOpen, Calendar, Network, Trophy,
  Shield, TrendingUp, Brain, Award, Globe, Sun, Moon,
  Play, CheckCircle, Sparkles, Target, Rocket, BarChart3,
  Building2, MapPin, Instagram, Linkedin, Facebook, Youtube,
} from 'lucide-react';

// Same handles as SkillNexus/PromptNexus — one NexusAI/TechIntelliVerse brand presence.
const SOCIAL_LINKS = [
  { Icon: Instagram, href: 'https://www.instagram.com/nexusai.india/',    label: 'Instagram' },
  { Icon: Linkedin,  href: 'https://www.linkedin.com/company/nexusaiweb/', label: 'LinkedIn' },
  { Icon: Facebook,  href: 'https://www.facebook.com/nexusaiweb/',        label: 'Facebook' },
  { Icon: Youtube,   href: 'https://www.youtube.com/@internnexus',        label: 'YouTube' },
];
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useTheme } from '@/context/ThemeContext';
import { cn } from '@/lib/utils';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};
const staggerContainer = {
  visible: { transition: { staggerChildren: 0.1 } }
};

const STATS = [
  { label: 'Startups Onboarded', value: '4,800+' },
  { label: 'Interns Hired', value: '32,000+' },
  { label: 'AI Matches Made', value: '1.2M+' },
  { label: 'Bihar Talent Pool', value: '85,000+' },
];

const FEATURES = [
  { icon: Brain, title: 'AI Intern Matching', desc: 'ML-powered AI Readiness Score matches the right interns to your startup roles instantly.', color: 'text-chart-1', bg: 'bg-chart-1/10' },
  { icon: Briefcase, title: 'Smart Job Posting', desc: 'Post roles, assign skill tasks, and access deep candidate pools with integrated interview rooms.', color: 'text-chart-2', bg: 'bg-chart-2/10' },
  { icon: BookOpen, title: 'PromptNexus Upskilling', desc: 'Enroll your entire team in AI upskilling programs with content SLAs and literacy scores.', color: 'text-chart-3', bg: 'bg-chart-3/10' },
  { icon: Calendar, title: 'EventNexus Hackathons', desc: 'Create, manage, and co-host hackathons and events with built-in analytics and registrations.', color: 'text-chart-4', bg: 'bg-chart-4/10' },
  { icon: Network, title: 'PartnerNexus', desc: 'Connect with incubators, discover investors, and access mentors — all in one ecosystem.', color: 'text-chart-5', bg: 'bg-chart-5/10' },
  { icon: Shield, title: 'Trust & Compliance', desc: 'DPIIT, GST & PAN verification, auto-generated intern agreements, and digital document vault.', color: 'text-primary', bg: 'bg-primary/10' },
];

const ECOSYSTEM = [
  { name: 'InternNexus', desc: 'Intern Discovery & Hiring' },
  { name: 'JobNexus', desc: 'Full-time Talent Acquisition' },
  { name: 'PromptNexus', desc: 'AI Upskilling Platform' },
  { name: 'SkillNexus', desc: 'Structured Team Training' },
  { name: 'EventNexus', desc: 'Events & Hackathons' },
  { name: 'PartnerNexus', desc: 'Investor & Mentor Network' },
  { name: 'ExamNexus', desc: 'Custom Assessment Engine' },
  { name: 'MentorNexus', desc: 'Expert Mentorship Access' },
];

const PRICING = [
  {
    name: 'Free',
    price: '₹0',
    period: '/month',
    desc: 'Perfect for early-stage startups',
    highlight: false,
    features: [
      '3 active internship postings',
      '3 interns/month',
      '5 intern pool views/day',
      '5 employees on PromptNexus',
      '1 event/month',
      '2 team members',
      'Standard certificates',
      'Help centre support',
    ],
    cta: 'Get Started Free',
  },
  {
    name: 'Plus',
    price: '₹2,999',
    period: '/month',
    desc: 'For growing startups scaling fast',
    highlight: true,
    badge: 'Most Popular',
    features: [
      '10 active internship postings',
      '10 interns/month',
      '50 intern pool views/day',
      '25 employees on PromptNexus',
      '5 events/month',
      '5 team members',
      'Branded certificates + PPO',
      'Interview rooms + email support',
    ],
    cta: 'Start Plus Plan',
  },
  {
    name: 'Pro',
    price: '₹7,999',
    period: '/month',
    desc: 'Full power for established startups',
    highlight: false,
    badge: 'Enterprise',
    features: [
      'Unlimited postings & interns',
      'Real-time AI match recommendations',
      'Unlimited PromptNexus seats',
      'Custom SkillNexus batches',
      'Unlimited events & hackathons',
      'Custom ExamNexus sessions',
      'DPIIT API + GST + PAN verified',
      'Priority support + account manager',
    ],
    cta: 'Start Pro Plan',
  },
];

const TESTIMONIALS = [
  {
    name: 'Anjali Mehta',
    role: 'Founder, AgriTech Bihar',
    text: 'StartupNexus completely transformed how we hire interns. The AI matching saved us weeks of screening, and the Bihar talent pool is genuinely exceptional.',
    avatar: 'AM',
    plan: 'Pro',
    rating: 5,
  },
  {
    name: 'Kunal Sinha',
    role: 'CTO, FinFlow Systems',
    text: 'The PromptNexus upskilling platform is unreal. Our team\'s AI readiness went from 34% to 78% in just 3 months. The dashboard analytics are chef-level.',
    avatar: 'KS',
    plan: 'Plus',
    rating: 5,
  },
  {
    name: 'Riya Verma',
    role: 'HR Lead, EduSpark',
    text: 'From intern letters to DPIIT verification to hackathon hosting — everything in one place. The gamification keeps our hiring team motivated too!',
    avatar: 'RV',
    plan: 'Pro',
    rating: 5,
  },
];

const FAQS = [
  { q: 'What makes StartupNexus different from regular job boards?', a: 'StartupNexus is a full startup ecosystem, not just a job board. We offer AI-powered intern matching, team upskilling, event management, partner discovery, gamification, and DPIIT-verified compliance tools — all tailored for Indian startups.' },
  { q: 'How does the AI Intern Matching Engine work?', a: 'Our AI computes an AI Readiness Score for each intern based on skills, projects, and learning signals. It then matches interns to your specific role requirements with explainability scores, so you understand why each match was suggested.' },
  { q: 'Can I switch plans anytime?', a: 'Yes! You can upgrade or downgrade at any time. Proration is calculated daily, so you only pay for what you use. Downgrades take effect at the end of your current billing cycle.' },
  { q: 'Is there a Bihar-specific talent pool?', a: 'Absolutely. StartupNexus has a dedicated Bihar local talent pipeline with exam calendar visibility, government scheme mapping, and direct access to students from top Bihar universities and polytechnics.' },
  { q: 'How does Razorpay payment integration work?', a: 'We use Razorpay for secure plan payments. You can pay via card, UPI, or net banking. All transactions are encrypted and you receive instant payment confirmation with invoice generation.' },
  { q: 'What is the TCI Program?', a: 'TCI (Talent-Culture-Impact) Program allows you to structure formal internship tracks with milestones, feedback cycles, and PPO (Pre-Placement Offer) issuance for standout interns.' },
];

// Landing Nav Component
const LandingNav = () => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <nav className={cn(
      'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      scrolled ? 'bg-background/95 backdrop-blur-md border-b border-border shadow-sm' : 'bg-transparent'
    )}>
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
            <Zap className="w-4 h-4 text-primary-foreground" />
          </div>
          <span className="font-bold text-foreground text-lg tracking-tight">StartupNexus</span>
        </div>

        <div className="hidden md:flex items-center gap-8">
          {['Features', 'Ecosystem', 'Pricing', 'About'].map(item => (
            <a key={item} href={`#${item.toLowerCase()}`}
               className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              {item}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>Sign In</Button>
          <Button size="sm" onClick={() => navigate('/onboarding')}
            className="gradient-primary text-primary-foreground border-0 hover:opacity-90 transition-opacity">
            Get Started Free
          </Button>
        </div>
      </div>
    </nav>
  );
};

// FAQ Item
const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border border-border rounded-xl overflow-hidden cursor-pointer"
      onClick={() => setOpen(p => !p)}
    >
      <div className="flex items-center justify-between p-5 hover:bg-muted/50 transition-colors">
        <span className="font-medium text-foreground text-sm">{q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
      </div>
      {open && (
        <div className="px-5 pb-5 text-sm text-muted-foreground border-t border-border pt-4">{a}</div>
      )}
    </div>
  );
};

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <LandingNav />

      {/* ===== HERO ===== */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-16">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxBSSUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzc5NTkwMjk0fDA&ixlib=rb-4.1.0&q=85"
            alt="AI Technology"
            className="w-full h-full object-cover opacity-20 dark:opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-background" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-transparent to-background/60" />
        </div>

        {/* Glow orb */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-10 dark:opacity-15"
          style={{ background: 'radial-gradient(circle, hsl(263 70% 50% / 0.6), transparent 70%)' }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <motion.div
              initial="hidden" animate="visible"
              variants={staggerContainer}
            >
              <motion.div variants={fadeUp} className="mb-6">
                <span className="badge-primary">
                  <Sparkles className="w-3 h-3" /> NexusAI Ecosystem v1.1
                </span>
              </motion.div>

              <motion.h1
                variants={fadeUp}
                className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight text-foreground mb-6"
              >
                Power Your Startup's Growth with{' '}
                <span className="gradient-text">AI-Driven</span>
                {' '}Talent Intelligence
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="text-lg text-muted-foreground mb-8 leading-relaxed max-w-2xl"
              >
                StartupNexus is the all-in-one platform for Indian startups to hire interns, recruit talent,
                upskill teams, manage events, and grow through the NexusAI ecosystem.
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-4 mb-12">
                <Button
                  size="lg"
                  className="gradient-primary text-primary-foreground border-0 hover:opacity-90 transition-opacity h-12 px-8 text-base font-semibold"
                  onClick={() => navigate('/onboarding')}
                >
                  Start Free Today <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" size="lg" className="h-12 px-8 text-base gap-2 border-border">
                  <Play className="w-4 h-4 fill-current" /> Watch Demo
                </Button>
              </motion.div>

              <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-success" /> No credit card required</span>
                <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-success" /> Free plan forever</span>
                <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-success" /> DPIIT recognized</span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="border-y border-border bg-card/50">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-3xl font-bold text-foreground mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section id="features" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden" whileInView="visible" variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div variants={fadeUp}>
              <span className="badge-primary mb-4 inline-flex"><Target className="w-3 h-3" /> Core Features</span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-foreground mb-4">
              Everything your startup needs to grow
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From AI-powered intern matching to team upskilling and investor discovery — built for the Indian startup ecosystem.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-elegant transition-shadow duration-300 border-border group">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center mb-4', feature.bg)}>
                      <feature.icon className={cn('w-5 h-5', feature.color)} />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed flex-1">{feature.desc}</p>
                    <div className="mt-4 flex items-center gap-1 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                      Learn more <ArrowRight className="w-3 h-3" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ECOSYSTEM ===== */}
      <section id="ecosystem" className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden" whileInView="visible" variants={staggerContainer}
            viewport={{ once: true }} className="text-center mb-16"
          >
            <motion.div variants={fadeUp}>
              <span className="badge-info mb-4 inline-flex"><Globe className="w-3 h-3" /> NexusAI Ecosystem</span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-foreground mb-4">
              One platform. Eight powerful products.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
              StartupNexus integrates seamlessly with every product in the NexusAI ecosystem.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {ECOSYSTEM.map((item, i) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.06 }}
                viewport={{ once: true }}
                className="p-5 rounded-xl border border-border bg-card hover:border-primary/30 hover:bg-accent/40 transition-all duration-200 cursor-pointer group"
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-colors">
                  <Zap className="w-4 h-4 text-primary" />
                </div>
                <div className="font-semibold text-sm text-foreground mb-1">{item.name}</div>
                <div className="text-xs text-muted-foreground">{item.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BIHAR SECTION ===== */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="badge-success mb-4 inline-flex"><MapPin className="w-3 h-3" /> Bihar-First Initiative</span>
              <h2 className="text-4xl font-bold text-foreground mb-6">
                Tap into Bihar's <span className="gradient-text">85,000+ talent pool</span>
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                India's fastest-growing startup talent pipeline is right here. StartupNexus gives you exclusive access to
                Bihar's top engineering and tech talent, with exam calendar visibility, government scheme mapping, and
                local talent classification.
              </p>
              <div className="space-y-3">
                {['Bihar Champion badge for top hiring startups', 'Access to BSSC, BPSC exam calendar & talent windows', 'Government scheme eligibility mapping', 'Direct college partnership network'].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="text-sm text-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden border border-border">
                <img
                  src="https://images.pexels.com/photos/7495111/pexels-photo-7495111.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Bihar Talent"
                  className="w-full h-72 object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-card border border-border rounded-xl p-4 shadow-card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-success" />
                  </div>
                  <div>
                    <div className="font-bold text-foreground">+240%</div>
                    <div className="text-xs text-muted-foreground">Bihar hiring rate YoY</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section id="pricing" className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden" whileInView="visible" variants={staggerContainer}
            viewport={{ once: true }} className="text-center mb-16"
          >
            <motion.div variants={fadeUp}>
              <span className="badge-primary mb-4 inline-flex"><Rocket className="w-3 h-3" /> Pricing Plans</span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-foreground mb-4">
              Start free, scale as you grow
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-muted-foreground">
              All plans include core hiring features. Upgrade for more power.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PRICING.map((plan, i) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className={cn(
                  'h-full flex flex-col relative overflow-hidden border',
                  plan.highlight
                    ? 'border-primary shadow-glow scale-105'
                    : 'border-border'
                )}>
                  {plan.highlight && (
                    <div className="absolute top-0 left-0 right-0 h-1 gradient-primary" />
                  )}
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-bold text-lg text-foreground">{plan.name}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">{plan.desc}</p>
                      </div>
                      {plan.badge && (
                        <span className={cn('text-xs font-medium px-2.5 py-1 rounded-full',
                          plan.highlight ? 'bg-primary/15 text-primary border border-primary/25' : 'bg-warning/10 text-warning border border-warning/25'
                        )}>{plan.badge}</span>
                      )}
                    </div>

                    <div className="flex items-baseline gap-1 mb-6">
                      <span className="text-3xl font-bold text-foreground">{plan.price}</span>
                      <span className="text-muted-foreground text-sm">{plan.period}</span>
                    </div>

                    <ul className="space-y-2.5 mb-8 flex-1">
                      {plan.features.map(f => (
                        <li key={f} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{f}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      className={cn(
                        'w-full mt-auto',
                        plan.highlight
                          ? 'gradient-primary text-primary-foreground border-0 hover:opacity-90'
                          : ''
                      )}
                      variant={plan.highlight ? 'default' : 'outline'}
                      onClick={() => navigate('/onboarding')}
                    >
                      {plan.cta}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden" whileInView="visible" variants={staggerContainer}
            viewport={{ once: true }} className="text-center mb-16"
          >
            <motion.div variants={fadeUp}>
              <span className="badge-warning mb-4 inline-flex"><Star className="w-3 h-3" /> Testimonials</span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-foreground mb-4">
              Loved by 4,800+ startups
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-border flex flex-col">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex items-center gap-1 mb-4">
                      {Array.from({ length: t.rating }).map((_, j) => (
                        <Star key={j} className="w-4 h-4 fill-warning text-warning" />
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-6">"{t.text}"</p>
                    <div className="flex items-center gap-3 mt-auto">
                      <div className="w-9 h-9 rounded-full gradient-primary flex items-center justify-center text-xs font-bold text-primary-foreground">
                        {t.avatar}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground">{t.name}</div>
                        <div className="text-xs text-muted-foreground">{t.role}</div>
                      </div>
                      <span className={cn('ml-auto text-xs font-medium px-2 py-0.5 rounded-full',
                        t.plan === 'Pro' ? 'plan-pro' : 'plan-plus'
                      )}>{t.plan}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial="hidden" whileInView="visible" variants={staggerContainer}
            viewport={{ once: true }} className="text-center mb-12"
          >
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-foreground mb-4">Frequently Asked Questions</motion.h2>
          </motion.div>
          <div className="space-y-3">
            {FAQS.map((faq) => <FAQItem key={faq.q} {...faq} />)}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-2xl overflow-hidden p-16 text-center"
            style={{ background: 'linear-gradient(135deg, hsl(263 70% 50% / 0.08), hsl(280 60% 55% / 0.06))' }}
          >
            <div className="absolute inset-0 border border-primary/15 rounded-2xl" />
            <Rocket className="w-12 h-12 text-primary mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Ready to build your dream startup team?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              Join 4,800+ Indian startups on StartupNexus. Start free, no credit card required.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button
                size="lg"
                className="gradient-primary text-primary-foreground border-0 hover:opacity-90 h-12 px-8 font-semibold"
                onClick={() => navigate('/onboarding')}
              >
                Get Started Free <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" size="lg" className="h-12 px-8 border-border">
                Schedule a Demo
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-8">
            <div className="col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 rounded-lg gradient-primary flex items-center justify-center">
                  <Zap className="w-3.5 h-3.5 text-primary-foreground" />
                </div>
                <span className="font-bold text-foreground">StartupNexus</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                AI-powered ecosystem for Indian startups.
                Part of TechIntelliVerse Private Limited.
              </p>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-primary" />
                <span className="text-xs text-muted-foreground">DPIIT Recognized Startup Platform</span>
              </div>
            </div>
            {[['Product', ['Dashboard', 'Intern Hiring', 'Job Hiring', 'Upskilling', 'Events']],
              ['Ecosystem', ['InternNexus', 'JobNexus', 'PromptNexus', 'SkillNexus', 'EventNexus']],
              ['Company', ['About Us', 'Blog', 'Careers', 'Press', 'Contact']]].map(([title, items]) => (
              <div key={title}>
                <h4 className="font-semibold text-foreground text-sm mb-3">{title}</h4>
                <ul className="space-y-2">
                  {items.map(item => (
                    <li key={item}><a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">{item}</a></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-border pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">© 2026 TechIntelliVerse Pvt. Ltd. All rights reserved.</p>
            <div className="flex items-center gap-3">
              {SOCIAL_LINKS.map(({ Icon, href, label }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                  className="w-7 h-7 rounded-lg bg-muted border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors">
                  <Icon size={14} />
                </a>
              ))}
            </div>
            <div className="flex items-center gap-4">
              {['Privacy Policy', 'Terms of Service', 'Cookie Policy'].map(item => (
                <a key={item} href="#" className="text-xs text-muted-foreground hover:text-foreground transition-colors">{item}</a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
